import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

/**
 * Map a {@link FetchBaseQueryError} from the users microservice to the
 * legacy {@code SC_*} status string the existing UI call sites switch on.
 *
 * The microservice returns JSON like
 * {@code { message: "...", errorCode: "OLD_PASSWORD_WRONG" }} with a non-2xx
 * HTTP status. Older windfall endpoints returned a 200 with
 * {@code CallHeader.StatusCode = "SC_ERROR_OLD_PASSWORD_IS_WRONG"}. We translate
 * between the two so callers (mutation switch-cases) don't have to be rewritten.
 */
export const mapUsersServiceError = (error: FetchBaseQueryError | undefined): string => {
  if (!error) return "SC_ERROR_UNKNOWN";

  if ("status" in error) {
    const body = error.data as { errorCode?: string; message?: string } | undefined;
    const code = body?.errorCode;

    if (code) {
      switch (code) {
        case "OLD_PASSWORD_WRONG":
          return "SC_ERROR_OLD_PASSWORD_IS_WRONG";
        case "WEAK_PASSWORD":
          return "SC_ERROR_WEAK_PASSWORD";
        case "PASSWORD_USED":
          return "SC_ERROR_PASSWORD_USED";
        case "INVALID_USER":
          return "SC_ERROR_INVALID_USER";
        case "UNAUTHORIZED_USER":
          return "SC_ERROR_UNAUTHORIZED_USER";
        case "INCOMPLETE_INFORMATION":
          return "SC_ERROR_INCOMPLETE_INFORMATION";
        case "SYSTEM_ERROR":
          return "SC_ERROR_SYSTEM_ERROR";
        default:
          break;
      }
    }

    const message = (body?.message ?? "").toLowerCase();

    if (error.status === 401) return "SC_ERROR_INVALID_USER";
    if (error.status === 403) return "SC_ERROR_UNAUTHORIZED_USER";
    if (error.status === 409) {
      if (message.includes("primary account email") || message.includes("primary email")) {
        return "SC_ERROR_EMAIL_DUPLICATES";
      }
      return "SC_ERROR_EMAIL_ALREADY_EXISTS";
    }
    if (error.status === 404) {
      // CCEmailService throws ResponseStatusException(NOT_FOUND, "CC email not found")
      // when the requested dle_id row doesn't exist for the caller. That is the
      // only DLE-specific 404 the backend emits; "User not found" 404s from the
      // same service are caller-lookup failures, not DLE errors.
      if (message === "cc email not found") return "SC_ERROR_INVALID_DLEID";
      return "SC_ERROR_NOT_FOUND";
    }
    if (error.status === 400) {
      if (message.includes("invalid characters")) {
        return "SC_ERROR_INVALID_BUSINESS_OTHER_TEXT";
      }
      if (message.includes("30 characters") || message.includes("limit")) {
        return "SC_ERROR_LIMIT_EXCEEDED";
      }
      if (message.includes("valid comma-separated email list") || message.includes("valid format")) {
        return "SC_ERROR_INVALID_FORMAT";
      }
      return "SC_ERROR_INCOMPLETE_INFORMATION";
    }
    if (typeof error.status === "number" && error.status >= 500) return "SC_ERROR_SYSTEM_ERROR";
  }

  return "SC_ERROR_UNKNOWN";
};
